import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "src/Components/Button/Button";
import { InfoMessageType } from "src/Components/InfoBar/InfoBar";
import PopupDialog from "src/SharedComponents/PopupDialog/PopupDialog";
import { UpgradeErrorCode, useGetUpgradeInfo } from "src/Utils/Hooks/Payments/useGetUpgradeInfo";
import { formatUpgradeDate } from "src/Utils/UpgradeDateFormatter/UpgradeDateFormatter";
import UpgradeErrorInfo from "./UpgradeErrorInfo";

interface IUpgradeInfo {
    onCloseUpgradeInfo: () => void;
    clinicId: string;
    priceId: string;
    setInfo: (message: string, type: InfoMessageType) => void;
    onConfirmUpgrade: (upcomingInvoiceId: string) => void;
}

const UpgradeInfo: React.FC<IUpgradeInfo> = ({ onCloseUpgradeInfo, clinicId, priceId, setInfo, onConfirmUpgrade }) => {
    const { t } = useTranslation();
    const { getUpgradeInfo, data, error } = useGetUpgradeInfo();
    const dividingFactor: number = 100;


    useEffect(() => {
        getUpgradeInfo(clinicId, priceId);
    }, [clinicId, priceId, getUpgradeInfo]);

    useEffect(() => {
        if (error) {
            setInfo(error, InfoMessageType.error);
        }
    }, [error, setInfo]);


    return (
        <>
            {
            data && data?.upgradeErrorCode === UpgradeErrorCode.None &&
                <PopupDialog onClose={() => onCloseUpgradeInfo()} marginTop="200px" popupWidth="496px">
                    <div className="p-3">
                        <div className="row">
                            <div className="col-md-10">
                                <span style={{ fontWeight: '600', fontSize: '20px' }}>
                                    {t('UpgradingDetails')}
                                </span>
                            </div>
                        </div>


                                <>
                                    <div className="row mt-4">
                                        <span style={{ fontSize: '14px' }}>{t('UpgradeDesc')}</span>
                                    </div>
                                    <>
                                        <div className="row mt-4 d-flex" style={{ fontSize: '14px' }}>
                                            <span style={{ flex: 2 }}>{data?.upgradeLicensePackageName}</span>
                                            <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>{data?.upgradePackageAmount / dividingFactor} {data?.currency}</span>
                                        </div>
                                        <div className="row" style={{ fontSize: '14px' }}>
                                            <span className="ds-text-primary">{t('ValidUntil')} {formatUpgradeDate(data?.upgradePackageEndDate)}</span>
                                        </div>
                                    </>
                                    <>
                                        <div className="row mt-4 d-flex" style={{ fontSize: '14px' }}>
                                            <span style={{ flex: 2 }}>{data?.currentLicensePackageName} - {t('Credit')}</span>
                                            <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>-{data?.creditAmount / dividingFactor} {data?.currency}</span>
                                        </div>
                                        <div className="row mb-4" style={{ fontSize: '14px' }}>
                                            <span className="ds-text-primary">{t('UnusedTime')} {formatUpgradeDate(data?.upgradeInitiateDate)} - {formatUpgradeDate(data?.currentPackageEndDate)}</span>
                                        </div>
                                    </>
                                    {
                                        data?.totalTax > 0 &&
                                        <>
                                            <div className="row mt-4 d-flex" style={{ fontSize: '14px' }}>
                                                <span style={{ flex: 2 }}>{t('TotalExcludingTax')}</span>
                                                <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>{data?.taxableAmount / dividingFactor} {data?.currency}</span>
                                            </div>
                                            <div className="row mt-4 d-flex" style={{ fontSize: '14px' }}>
                                                <span style={{ flex: 2 }}>{data?.taxDescription} on {data?.taxableAmount / dividingFactor}</span>
                                                <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>{data?.totalTax / dividingFactor} {data?.currency}</span>
                                            </div>
                                        </>
                                    }
                                    {
                                      data?.appliedBalance < 0 &&
                                       <>
                                        <div className="row mt-4 d-flex" 
                                             style={{ fontSize: '14px' }}>
                                               <span style={{ flex: 2 }}>{t('AppliedBalance')}</span>
                                               <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end' }}>{data?.appliedBalance / dividingFactor} {data?.currency}</span>
                                        </div>  
                                        </>
                                    }
                                    <div className="row mt-2 d-flex" style={{ fontSize: '14px', borderTop: '1px solid #E0E0E0', paddingTop: '25px', marginLeft: '0px', marginRight: '20px', fontWeight: 'bold' }}>
                                        <span style={{ flex: 2, paddingLeft: 0 }}>{t('AmountDue')}</span>
                                        <span className="d-flex" style={{ flex: 1, justifyContent: 'flex-end', paddingRight: 0 }}>{data?.amountDue / dividingFactor} {data?.currency}</span>
                                    </div>
                                    <div className="row mt-5 text-center">
                                        <div className="ms-3">
                                            <Button
                                                fontWeight="500"
                                                fontSize="14px"
                                                text={t('ConfirmProceed')}
                                                onClick={() => onConfirmUpgrade(data?.upcomingInvoiceId)}
                                            />
                                        </div>
                                    </div>
                                </>
                            
                        
                    </div>
                </PopupDialog>
     }       
            {
                data && data?.upgradeErrorCode !== UpgradeErrorCode.None &&
                <UpgradeErrorInfo
                    code={data.upgradeErrorCode}
                    clinicId={clinicId}
                    setInfo={setInfo}
                    updatePageLoader={() => {}}
                    onCloseUpgradeInfo={onCloseUpgradeInfo}
                />
            }
            
        </>
    );
}

export default UpgradeInfo;